import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ROUTES } from 'shared/constants';
import { formatTitleCase } from 'shared/utils';
import qualityMetricApi from 'shared/api/quality-metric.service.api';
import { Loader } from 'src/components/core';
import { NotFound } from 'src/components/common';
import { CaseTeamColumn } from './case-team-column';
import { CaseInfoColumn } from './case-info-column';
import { CaseComplianceColumn } from './case-compliance-column';
import styles from './case-detail.module.scss';

export function CaseDetail() {
  const { id: caseId } = useParams();
  const lang = useSelector(getLang('QUALITY_METRIC'));
  const sidebarLang = useSelector(getLang('SIDEBAR'));
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCase = async () => {
      if (!caseId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await qualityMetricApi.getCasesById({
          caseId,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        setLoading(false);

        if (fetchError) {
          console.error('Error fetching case:', fetchError);
          setError(fetchError);
          return;
        }

        if (data?.case) {
          setCaseData(data.case);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Unexpected error fetching case:', err);
        setLoading(false);
        setError(err);
      }
    };

    fetchCase();
    return () => {
      controller.abort();
    };
  }, [caseId]);

  if (loading) {
    return <Loader />;
  }

  if (error || !caseData) {
    return <NotFound text={lang.NO_DATA} />;
  }

  const caseTitle = formatTitleCase(caseData.metadata?.procedure) || caseData.externalId;

  return (
    <div className={styles.container}>
      <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
        <Link to={ROUTES.HOME} className={styles.breadcrumbLink}>
          {sidebarLang.HOME}
        </Link>
        <FontAwesomeIcon icon={faChevronRight} className={styles.breadcrumbArrow} />
        <Link to={ROUTES.QUALITY_METRIC} className={styles.breadcrumbLink}>
          {lang.METRICS_HEADER}
        </Link>
      </nav>
      <h2 className={styles.title}>{caseTitle}</h2>
      <div className={styles.columns}>
        <CaseTeamColumn participants={caseData.participants} lang={lang} />
        <CaseInfoColumn caseData={caseData} lang={lang} />
        <CaseComplianceColumn metrics={caseData.metrics} lang={lang} />
      </div>
    </div>
  );
}
